import { FC } from 'react';
import Modal from '../../../../components/Modal';
import CreateSessionForm from '../forms/CreateSessionForm';
import {
  useCreateManualClientSessionMutation,
  CreateManualClientSessionMutation,
  CreateManualClientSessionInput,
  Patient,
} from '../../../../../generated/graphql';
import { customToast } from '../../../../components/ToastAlert/customToast';
import { logger } from '../../../../../lib/logger';

export type CreatedSession =
  CreateManualClientSessionMutation['createManualClientSession']['clientSession'];

interface CreateSessionModalProps {
  onClose: () => void;
  onSuccess: (createdSession?: CreatedSession) => void;
  initialPatient?: Patient | null;
}

const CreateSessionModal: FC<CreateSessionModalProps> = ({
  onClose,
  onSuccess,
  initialPatient = null,
}) => {
  const [createSession, { loading: isSubmitting }] =
    useCreateManualClientSessionMutation();

  const handleSubmit = async (formData: CreateManualClientSessionInput) => {
    try {
      const response = await createSession({
        variables: {
          input: formData,
        },
      });

      if (response.data?.createManualClientSession.success) {
        customToast.success('Session created successfully');
        const createdSession =
          response.data.createManualClientSession.clientSession;
        onSuccess(createdSession);
        onClose();
        return {
          success: true,
          errors: [],
        };
      } else {
        const errorMessage =
          response.data?.createManualClientSession.error ||
          'Failed to create session';
        customToast.error(errorMessage);
        // Return success: false to prevent form from closing modal
        return {
          success: false,
          errors: response.data?.createManualClientSession.errors || [
            { field: 'root', message: errorMessage },
          ],
        };
      }
    } catch (error) {
      logger.error(error);
      customToast.error('Failed to create session');
      // Return success: false to prevent form from closing modal
      return {
        success: false,
        errors: [{ field: 'root', message: 'Failed to create session' }],
      };
    }
  };

  return (
    <Modal
      name="CreateSession"
      isOpen={true}
      setClosed={onClose}
      width="medium"
    >
      <div className="p-6">
        <CreateSessionForm
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isSubmitting}
          initialPatient={initialPatient}
        />
      </div>
    </Modal>
  );
};

export default CreateSessionModal;
